import { RentVsBuyVariables } from "./RentVsBuyCalculator.constants.esm.js";
const INTERPOLATION_END_MONTH = 61;
const MONTHS_PER_YEAR = 12;
const PERCENT_TO_RATIO = 0.01;
const RATES_LENGTH = 360;
const sumValues = (props, srcA, srcB, dest) => {
  let i;
  const {
    length
  } = props;
  let prop;
  for (i = 0; i < length; i += 1) {
    prop = props[i];
    dest[prop] = (srcA[prop] || 0) + (srcB[prop] || 0);
  }
  return dest;
};
const accumulateBuyingValues = (lastMonthCumulativeValues, currentMonthValues, mortgageBalance) => {
  const last = lastMonthCumulativeValues;
  const cur = currentMonthValues;
  const mortgagePrincipalAndInterest = last.mortgagePrincipalAndInterest + cur.mortgagePrincipalAndInterest;
  const interest = last.interest + cur.interest;
  const result = {
    equity: cur.homeValue - mortgageBalance,
    homeValue: cur.homeValue,
    interest,
    mortgagePrincipalAndInterest,
    principal: mortgagePrincipalAndInterest - interest,
    capitalGainsTaxRate: last.capitalGainsTaxRate,
    investmentReturnRate: last.investmentReturnRate,
    isFirstMonth: last.isFirstMonth,
    inflationFactor: last.inflationFactor,
    netCosts: 0,
    sunkCosts: 0,
    totalOutOfPocketCosts: 0,
    utilities: 0,
    hoaDues: 0,
    homeownersInsurance: 0,
    maintenanceCosts: 0,
    opportunityCosts: 0,
    pmi: 0,
    propertyTax: 0,
    renovationCosts: 0,
    taxBenefit: 0
  };
  sumValues(["hoaDues", "homeownersInsurance", "maintenanceCosts", "opportunityCosts", "pmi", "propertyTax", "renovationCosts", "taxBenefit", "totalOutOfPocketCosts", "utilities"], last, cur, result);
  return result;
};
const accumulateRentingValues = (lastMonthCumulativeValues, currentMonthValues) => {
  const result = {
    capitalGainsTaxRate: lastMonthCumulativeValues.capitalGainsTaxRate,
    investmentReturnRate: lastMonthCumulativeValues.investmentReturnRate,
    isFirstMonth: lastMonthCumulativeValues.isFirstMonth,
    inflationFactor: lastMonthCumulativeValues.inflationFactor,
    totalOutOfPocketCosts: 0,
    utilities: 0,
    netCosts: 0,
    sunkCosts: 0,
    opportunityCosts: 0,
    rentersInsurance: 0,
    rent: 0
  };
  sumValues(["opportunityCosts", "rentersInsurance", "rent", "totalOutOfPocketCosts", "utilities"], lastMonthCumulativeValues, currentMonthValues, result);
  return result;
};
const calculateTaxOnProfit = (capitalGainsTaxExclusion, capitalGainsTaxRate, homeValue, purchaseCosts, purchasePrice, renovationCosts, sellingCosts) => {
  const totalCosts = purchasePrice + purchaseCosts + renovationCosts + sellingCosts;
  const tax = capitalGainsTaxRate * PERCENT_TO_RATIO * (homeValue - totalCosts - capitalGainsTaxExclusion);
  return Math.max(tax, 0);
};
const calculateBuyingSunkCosts = (buyingValues, sellingValues, purchaseCosts) => {
  const buy = buyingValues;
  const sell = sellingValues;
  return buy.interest + purchaseCosts + buy.propertyTax + buy.maintenanceCosts + buy.pmi + buy.hoaDues + buy.homeownersInsurance + sell.sellingCosts - sell.tax;
};
const calculateRentingSunkCosts = (rentingValues, brokerFee) => {
  return rentingValues.rent + rentingValues.utilities + rentingValues.rentersInsurance + brokerFee;
};
const calculateNetBuyingCosts = (cumulativeBuyingValues, sellingValues) => {
  const buy = cumulativeBuyingValues;
  const sell = sellingValues;
  return buy.totalOutOfPocketCosts + sell.sellingCosts + sell.tax + buy.opportunityCosts - buy.equity;
};
const addCompositeBuyingCosts = (cumulativeValues, sellingValues, purchaseCosts) => {
  cumulativeValues.netCosts = calculateNetBuyingCosts(cumulativeValues, sellingValues);
  cumulativeValues.sunkCosts = calculateBuyingSunkCosts(cumulativeValues, sellingValues, purchaseCosts);
};
const calculateNetRentingCosts = (cumulativeRentingValues, rentDeposit) => {
  const rent = cumulativeRentingValues;
  return rent.totalOutOfPocketCosts + rent.opportunityCosts - rentDeposit;
};
const addCompositeRentingCosts = (cumulativeRentingValues, rentBrokerFee, rentDeposit) => {
  cumulativeRentingValues.netCosts = calculateNetRentingCosts(cumulativeRentingValues, rentDeposit);
  cumulativeRentingValues.sunkCosts = calculateRentingSunkCosts(cumulativeRentingValues, rentBrokerFee);
};
const calculateTaxBenefit = (inflationFactor, interest, marginalTaxRate, propertyTax, standardDeduction) => {
  const totalDeduction = interest + propertyTax - standardDeduction * inflationFactor / MONTHS_PER_YEAR;
  const monthlyTaxBenefit = totalDeduction * marginalTaxRate * PERCENT_TO_RATIO;
  return Math.max(monthlyTaxBenefit, 0);
};
const annualRateToMonthlyFactor = (rate) => (1 + rate * PERCENT_TO_RATIO) ** (1 / MONTHS_PER_YEAR) - 1;
const calculateBuyingOutOfPocketCosts = (params) => {
  const {
    downPayment,
    hoaDues,
    homeownersInsurance,
    isFirstMonth,
    maintenanceCosts,
    mortgagePrincipalAndInterest,
    pmi,
    propertyTax,
    purchaseCosts,
    renovationCosts,
    taxBenefit,
    utilitiesCosts
  } = params;
  return (isFirstMonth ? downPayment + purchaseCosts : 0) + hoaDues + homeownersInsurance + maintenanceCosts + mortgagePrincipalAndInterest + pmi + propertyTax + renovationCosts + utilitiesCosts - taxBenefit;
};
const calculateOpportunityCosts = (cumulativeTotalOutOfPocketCosts, lastMonthCumulativeOpportunityCosts, investmentReturnRate, capitalGainsTaxRate) => (lastMonthCumulativeOpportunityCosts + cumulativeTotalOutOfPocketCosts) * annualRateToMonthlyFactor(investmentReturnRate) * (1 - capitalGainsTaxRate * PERCENT_TO_RATIO);
const interpolateSpline = (startValue, endValue, splineEndYear, totalLength) => {
  let i;
  let t;
  const result = [];
  for (i = 0; i < splineEndYear; i += 1) {
    t = i / (splineEndYear - 1);
    result[i] = startValue * (2 * t * t * t - 3 * t * t + 1) + endValue * (-2 * t * t * t + 3 * t * t);
  }
  for (i = splineEndYear; i < totalLength; i += 1) {
    result[i] = endValue;
  }
  return result;
};
const buildBuyingTable = (params) => {
  const {
    capitalGainsTaxRate,
    homeownersInsurancePercent,
    investmentReturnRate,
    maintenanceCostsPercent,
    marginalTaxRate,
    pmi,
    propertyTaxRate,
    purchasePrice,
    renovationCostsPercent,
    standardDeduction,
    downPayment,
    homeAppreciationFactor,
    inflationFactor,
    interest,
    isFirstMonth,
    lastMonthCumulative,
    mortgagePrincipalAndInterest,
    propertyTaxHomeAppreciationFactor,
    purchaseCosts
  } = params;
  let {
    hoaDues
  } = params;
  let {
    buyingUtilities
  } = params;
  const homeValue = purchasePrice * homeAppreciationFactor;
  const propertyTaxHomeValue = purchasePrice * propertyTaxHomeAppreciationFactor;
  const propertyTax = propertyTaxRate * PERCENT_TO_RATIO / MONTHS_PER_YEAR * propertyTaxHomeValue;
  const monthlyMaintenanceCosts = maintenanceCostsPercent * PERCENT_TO_RATIO / MONTHS_PER_YEAR * purchasePrice * inflationFactor;
  const monthlyRenovationCosts = renovationCostsPercent * PERCENT_TO_RATIO / MONTHS_PER_YEAR * purchasePrice * inflationFactor;
  const monthlyHomeownersInsurance = homeownersInsurancePercent * PERCENT_TO_RATIO / MONTHS_PER_YEAR * homeValue;
  hoaDues *= inflationFactor;
  buyingUtilities *= inflationFactor;
  const taxBenefit = calculateTaxBenefit(inflationFactor, interest, marginalTaxRate, propertyTax, standardDeduction);
  const totalOutOfPocketCosts = calculateBuyingOutOfPocketCosts({
    downPayment,
    hoaDues,
    homeownersInsurance: monthlyHomeownersInsurance,
    isFirstMonth,
    maintenanceCosts: monthlyMaintenanceCosts,
    mortgagePrincipalAndInterest,
    propertyTax,
    purchaseCosts,
    renovationCosts: monthlyRenovationCosts,
    taxBenefit,
    utilitiesCosts: buyingUtilities,
    pmi
  });
  const opportunityCosts = calculateOpportunityCosts(lastMonthCumulative.totalOutOfPocketCosts + totalOutOfPocketCosts, lastMonthCumulative.opportunityCosts, investmentReturnRate, capitalGainsTaxRate);
  return {
    hoaDues,
    homeownersInsurance: monthlyHomeownersInsurance,
    homeValue,
    interest,
    maintenanceCosts: monthlyMaintenanceCosts,
    mortgagePrincipalAndInterest,
    pmi,
    opportunityCosts,
    principal: mortgagePrincipalAndInterest - interest,
    propertyTax,
    renovationCosts: monthlyRenovationCosts,
    taxBenefit,
    totalOutOfPocketCosts,
    utilities: buyingUtilities
  };
};
const buildRentingTable = (params) => {
  const {
    capitalGainsTaxRate,
    investmentReturnRate,
    initialMonthlyRent,
    rentBrokerFee,
    rentDeposit,
    rentersInsuranceRate,
    rentingUtilities,
    isFirstMonth,
    lastMonthCumulativeValues,
    rentAppreciationFactor,
    inflationFactor
  } = params;
  const monthlyRent = initialMonthlyRent * rentAppreciationFactor;
  const rentersInsurance = rentersInsuranceRate * PERCENT_TO_RATIO * monthlyRent;
  const utilities = rentingUtilities * inflationFactor;
  const totalOutOfPocketCosts = monthlyRent + rentersInsurance + utilities + (isFirstMonth ? rentBrokerFee + rentDeposit : 0);
  const opportunityCosts = calculateOpportunityCosts(lastMonthCumulativeValues.totalOutOfPocketCosts + totalOutOfPocketCosts, lastMonthCumulativeValues.opportunityCosts, investmentReturnRate, capitalGainsTaxRate);
  return {
    opportunityCosts,
    rentersInsurance,
    rent: monthlyRent,
    totalOutOfPocketCosts,
    utilities
  };
};
const buildMonthlyRates = (initialRate, longTermRate) => interpolateSpline(annualRateToMonthlyFactor(initialRate) / PERCENT_TO_RATIO, annualRateToMonthlyFactor(longTermRate) / PERCENT_TO_RATIO, INTERPOLATION_END_MONTH, RATES_LENGTH);
const buildSellingValues = (params) => {
  const {
    capitalGainsTaxExclusion,
    capitalGainsTaxRate,
    purchasePrice,
    sellingCostsPercent,
    mortgageBalance,
    purchaseCosts,
    renovationCosts,
    homeValue
  } = params;
  const sellingCosts = homeValue * sellingCostsPercent * PERCENT_TO_RATIO;
  const taxOnProfit = calculateTaxOnProfit(capitalGainsTaxExclusion, capitalGainsTaxRate, homeValue, purchaseCosts, purchasePrice, renovationCosts, sellingCosts);
  return {
    sellingCosts,
    remainingPrincipal: mortgageBalance,
    homeValue,
    tax: taxOnProfit
  };
};
const calculateMonthlyPrincipalAndInterest = (loanAmount, interestRate, loanTermMonths) => {
  const monthlyInterestFactor = interestRate * PERCENT_TO_RATIO / MONTHS_PER_YEAR;
  const compoundedInterestFactor = (1 + monthlyInterestFactor) ** loanTermMonths;
  const result = loanAmount * monthlyInterestFactor * compoundedInterestFactor / (compoundedInterestFactor - 1);
  return isNaN(result) ? 0 : result;
};
const calculatePmi = (purchasePrice, downPaymentPercent) => {
  let annualPmiToLoanRatio;
  const loanAmount = purchasePrice * (1 - downPaymentPercent * PERCENT_TO_RATIO);
  if (downPaymentPercent >= 20) annualPmiToLoanRatio = 0;
  else if (downPaymentPercent >= 15) annualPmiToLoanRatio = 44e-4;
  else if (downPaymentPercent >= 10) annualPmiToLoanRatio = 59e-4;
  else if (downPaymentPercent >= 5) annualPmiToLoanRatio = 76e-4;
  else annualPmiToLoanRatio = 98e-4;
  return annualPmiToLoanRatio * loanAmount / MONTHS_PER_YEAR;
};
const createAnnualRollup = (reduceFn, monthlyValues) => {
  let i;
  const years = monthlyValues.length / MONTHS_PER_YEAR;
  const annualValues = [];
  for (i = 0; i < years; i += 1) {
    annualValues[i] = monthlyValues.slice(i * MONTHS_PER_YEAR, (i + 1) * MONTHS_PER_YEAR).reduce(reduceFn);
  }
  return annualValues;
};
const ratesToCompoundedFactors = (rates) => {
  const compoundedFactors = [];
  let i;
  const {
    length
  } = rates;
  if (rates.length) {
    compoundedFactors[0] = 1 + rates[0] * PERCENT_TO_RATIO;
  }
  for (i = 1; i < length; i += 1) {
    compoundedFactors[i] = compoundedFactors[i - 1] * (1 + rates[i] * PERCENT_TO_RATIO);
  }
  return compoundedFactors;
};
const sumBuyingValues = (a, b) => {
  const result = {
    hoaDues: 0,
    homeownersInsurance: 0,
    homeValue: 0,
    interest: 0,
    maintenanceCosts: 0,
    mortgagePrincipalAndInterest: 0,
    pmi: 0,
    opportunityCosts: 0,
    principal: 0,
    propertyTax: 0,
    renovationCosts: 0,
    taxBenefit: 0,
    totalOutOfPocketCosts: 0,
    utilities: 0
  };
  sumValues(["hoaDues", "homeownersInsurance", "homeValue", "interest", "maintenanceCosts", "mortgagePrincipalAndInterest", "opportunityCosts", "pmi", "principal", "propertyTax", "renovationCosts", "taxBenefit", "totalOutOfPocketCosts", "utilities"], a, b, result);
  return result;
};
const sumRentingValues = (a, b) => {
  const result = {
    opportunityCosts: 0,
    rentersInsurance: 0,
    rent: 0,
    totalOutOfPocketCosts: 0,
    utilities: 0
  };
  sumValues(["opportunityCosts", "rent", "rentersInsurance", "totalOutOfPocketCosts", "utilities"], a, b, result);
  return result;
};
const useLastValue = (_a, b) => b;
const calculateFullBreakEven = (params) => {
  const {
    buyingUtilities,
    capitalGainsTaxExclusion,
    capitalGainsTaxRate,
    downPaymentPercent,
    hoaDues,
    homeownersInsurancePercent,
    initialHomeAppreciationRate,
    initialInflationRate,
    initialRentAppreciationRate,
    investmentReturnRate,
    loanTermYears,
    longTermHomeAppreciationRate,
    longTermInflationRate,
    longTermRentAppreciationRate,
    maintenanceCostsPercent,
    marginalTaxRate,
    monthlyRent,
    mortgageRate,
    propertyTaxRate,
    purchaseCostsPercent,
    purchasePrice,
    renovationCostsPercent,
    rentBrokerFee,
    rentDepositMonths,
    rentersInsuranceRate,
    rentingUtilities,
    sellingCostsPercent,
    standardDeduction
  } = params;
  const loanTermMonths = loanTermYears * MONTHS_PER_YEAR;
  const downPayment = downPaymentPercent * PERCENT_TO_RATIO * purchasePrice;
  const loanAmount = purchasePrice - downPayment;
  const mortgagePrincipalAndInterest = calculateMonthlyPrincipalAndInterest(loanAmount, mortgageRate, loanTermMonths);
  const pmi = calculatePmi(purchasePrice, downPaymentPercent);
  const purchaseCosts = purchaseCostsPercent * PERCENT_TO_RATIO * purchasePrice;
  const rentDeposit = rentDepositMonths * monthlyRent;
  let mortgageBalance = loanAmount;
  const buyingTable = [];
  const rentingTable = [];
  const sellingTable = [];
  const cumulativeBuyingTable = [];
  const cumulativeRentingTable = [];
  const cumulativeCostsTable = [];
  let breakEven = 0;
  let breakEvenInterpolated = 0;
  let cumulativeSavings = 0;
  let breakEvenFound = false;
  let month;
  let i;
  let lastMonthCumulativeBuying;
  let lastMonthCumulativeRenting;
  let previousSavings;
  let interpolatedPartialMonth;
  const monthlyHomeAppreciationRates = buildMonthlyRates(initialHomeAppreciationRate, longTermHomeAppreciationRate);
  const monthlyCompoundedHomeAppreciation = ratesToCompoundedFactors(monthlyHomeAppreciationRates);
  const monthlyRentAppreciationRates = buildMonthlyRates(initialRentAppreciationRate, longTermRentAppreciationRate);
  const monthlyCompoundedRentAppreciation = ratesToCompoundedFactors(monthlyRentAppreciationRates);
  const monthlyInflationRates = buildMonthlyRates(initialInflationRate, longTermInflationRate);
  const monthlyCompoundedInflation = ratesToCompoundedFactors(monthlyInflationRates);
  for (i = 0; i < loanTermMonths; i += 1) {
    month = i + 1;
    const inflationFactor = monthlyCompoundedInflation[i];
    const homeAppreciationFactor = monthlyCompoundedHomeAppreciation[i];
    let propertyTaxHomeAppreciationFactor;
    let currentYearRentAppreciationFactor;
    if (i >= MONTHS_PER_YEAR) {
      propertyTaxHomeAppreciationFactor = monthlyCompoundedHomeAppreciation[i - i % MONTHS_PER_YEAR - 1];
      currentYearRentAppreciationFactor = monthlyCompoundedRentAppreciation[i - i % MONTHS_PER_YEAR - 1];
    } else {
      propertyTaxHomeAppreciationFactor = 1;
      currentYearRentAppreciationFactor = 1;
    }
    const interest = mortgageRate * PERCENT_TO_RATIO / MONTHS_PER_YEAR * mortgageBalance;
    const principal = mortgagePrincipalAndInterest - interest;
    mortgageBalance -= principal;
    if (i !== 0) {
      lastMonthCumulativeBuying = cumulativeBuyingTable[i - 1];
      lastMonthCumulativeRenting = cumulativeRentingTable[i - 1];
    } else {
      lastMonthCumulativeBuying = {
        equity: 0,
        homeValue: 0,
        interest: 0,
        mortgagePrincipalAndInterest: 0,
        principal: 0,
        capitalGainsTaxRate,
        investmentReturnRate,
        isFirstMonth: true,
        inflationFactor: 1,
        totalOutOfPocketCosts: 0,
        utilities: 0,
        netCosts: 0,
        sunkCosts: 0,
        hoaDues: 0,
        homeownersInsurance: 0,
        maintenanceCosts: 0,
        opportunityCosts: 0,
        pmi: 0,
        propertyTax: 0,
        renovationCosts: 0,
        taxBenefit: 0
      };
      lastMonthCumulativeRenting = {
        capitalGainsTaxRate,
        investmentReturnRate,
        isFirstMonth: true,
        inflationFactor: 1,
        totalOutOfPocketCosts: 0,
        utilities: 0,
        netCosts: 0,
        sunkCosts: 0,
        opportunityCosts: 0,
        rentersInsurance: 0,
        rent: 0
      };
    }
    buyingTable[i] = buildBuyingTable({
      buyingUtilities,
      capitalGainsTaxRate,
      hoaDues,
      homeownersInsurancePercent,
      investmentReturnRate,
      maintenanceCostsPercent,
      marginalTaxRate,
      propertyTaxRate,
      purchasePrice,
      renovationCostsPercent,
      standardDeduction,
      downPayment,
      homeAppreciationFactor,
      inflationFactor,
      interest,
      isFirstMonth: i === 0,
      lastMonthCumulative: lastMonthCumulativeBuying,
      mortgagePrincipalAndInterest,
      propertyTaxHomeAppreciationFactor,
      purchaseCosts,
      pmi
    });
    rentingTable[i] = buildRentingTable({
      capitalGainsTaxRate,
      investmentReturnRate,
      initialMonthlyRent: monthlyRent,
      rentBrokerFee,
      rentDeposit,
      rentersInsuranceRate,
      rentingUtilities,
      isFirstMonth: i === 0,
      lastMonthCumulativeValues: lastMonthCumulativeRenting,
      rentAppreciationFactor: currentYearRentAppreciationFactor,
      inflationFactor
    });
    cumulativeBuyingTable[i] = accumulateBuyingValues(lastMonthCumulativeBuying, buyingTable[i], mortgageBalance);
    cumulativeRentingTable[i] = accumulateRentingValues(lastMonthCumulativeRenting, rentingTable[i]);
    sellingTable[i] = buildSellingValues({
      capitalGainsTaxExclusion,
      capitalGainsTaxRate,
      purchasePrice,
      sellingCostsPercent,
      mortgageBalance,
      purchaseCosts,
      renovationCosts: cumulativeBuyingTable[i].renovationCosts,
      homeValue: cumulativeBuyingTable[i].homeValue
    });
    addCompositeBuyingCosts(cumulativeBuyingTable[i], sellingTable[i], downPayment);
    addCompositeRentingCosts(cumulativeRentingTable[i], rentBrokerFee, rentDeposit);
    previousSavings = cumulativeSavings;
    cumulativeSavings = cumulativeBuyingTable[i].netCosts - cumulativeRentingTable[i].netCosts;
    if (cumulativeSavings <= 0 && !breakEvenFound) {
      breakEven = month;
      interpolatedPartialMonth = previousSavings / (previousSavings - cumulativeSavings);
      breakEvenInterpolated = breakEven - 1 + interpolatedPartialMonth;
      breakEvenFound = true;
    }
    cumulativeCostsTable[i] = {
      buyingValues: cumulativeBuyingTable[i],
      rentingValues: cumulativeRentingTable[i],
      savings: cumulativeSavings,
      sellingValues: sellingTable[i]
    };
  }
  const annualBuyingTable = createAnnualRollup(sumBuyingValues, buyingTable);
  const annualRentingTable = createAnnualRollup(sumRentingValues, rentingTable);
  const annualCumulativeCostsTable = createAnnualRollup(useLastValue, cumulativeCostsTable);
  for (i = 0; i < loanTermYears; i += 1) {
    annualBuyingTable[i].sunkCosts = calculateBuyingSunkCosts(annualBuyingTable[i], annualCumulativeCostsTable[i].sellingValues, purchaseCosts);
    annualRentingTable[i].sunkCosts = calculateRentingSunkCosts(annualRentingTable[i], i === 0 ? rentBrokerFee : 0);
  }
  return {
    annualBuyingValues: annualBuyingTable,
    annualCumulativeCosts: annualCumulativeCostsTable,
    annualRentingValues: annualRentingTable,
    breakEvenMonths: breakEven,
    breakEvenMonthsInterpolated: breakEvenInterpolated,
    totalSavings: cumulativeSavings,
    downPayment
  };
};
const calculateStepForSlider = (id, maxVal) => {
  switch (id) {
    case RentVsBuyVariables.MonthlyRent:
      return 100;
    case RentVsBuyVariables.PurchasePrice:
      return 1e4;
  }
  return maxVal / 100;
};
const calculateMaxValForSlider = (id, percentageQuestion) => {
  if (percentageQuestion) return 100;
  if (id === RentVsBuyVariables.MonthlyRent) return 1e4;
  if (id === RentVsBuyVariables.DownPaymentPercent) return 100;
  return 25e5;
};
const calculatedUpdatesForAVBInputs = (currentState) => {
  return {
    [RentVsBuyVariables.HomeownersInsurancePercent]: {
      stateKey: RentVsBuyVariables.HomeownersInsurancePercent,
      calculation: (value) => value / currentState.purchasePrice * 100
    },
    [RentVsBuyVariables.RenovationCostsPercent]: {
      stateKey: RentVsBuyVariables.RenovationCostsPercent,
      calculation: (value) => value / currentState.purchasePrice * 100
    },
    [RentVsBuyVariables.RentersInsuranceRate]: {
      stateKey: RentVsBuyVariables.RentersInsuranceRate,
      calculation: (value) => value / currentState.monthlyRent * 100
    }
  };
};
export {
  accumulateBuyingValues,
  accumulateRentingValues,
  addCompositeBuyingCosts,
  addCompositeRentingCosts,
  annualRateToMonthlyFactor,
  buildBuyingTable,
  buildMonthlyRates,
  buildRentingTable,
  buildSellingValues,
  calculateBuyingOutOfPocketCosts,
  calculateBuyingSunkCosts,
  calculateFullBreakEven,
  calculateMaxValForSlider,
  calculateMonthlyPrincipalAndInterest,
  calculateNetBuyingCosts,
  calculateNetRentingCosts,
  calculateOpportunityCosts,
  calculatePmi,
  calculateRentingSunkCosts,
  calculateStepForSlider,
  calculateTaxBenefit,
  calculateTaxOnProfit,
  calculatedUpdatesForAVBInputs,
  createAnnualRollup,
  interpolateSpline,
  ratesToCompoundedFactors,
  sumBuyingValues,
  sumRentingValues,
  useLastValue
};
